import { type UseFormReturnType, useForm } from '@mantine/form';
import dayjs from 'dayjs';
import type { MailsSearchParams } from 'modules/Mails/hooks/use-load-mails';
import { MailStatusSchema } from 'modules/Mails/types/email-status';

import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isEmptyish, omitBy } from 'remeda';
import { getSessionStorageItem } from 'shared/utils/session-storage';

export type MailFiltersForm = UseFormReturnType<MailsSearchParams>;
const STORAGE_KEY = 'mail-filters';

const parseUrlParams = (search: string): Partial<MailsSearchParams> => {
  const params = new URLSearchParams(search);
  const result: Partial<MailsSearchParams> = {};
  const statusParse = MailStatusSchema.safeParse(params.get('status'));
  const accountManagerId = params.get('account_manager_id');
  if (statusParse.success) result.status = statusParse.data;
  if (accountManagerId != null) result.accountManagerId = accountManagerId;
  return result;
};

export const useMailFilters = () => {
  const { search } = useLocation();

  const urlParams = parseUrlParams(search);

  const form = useForm<MailsSearchParams>({
    initialValues: {
      ...MAIL_FILTERS_INITIAL_VALUES,
      ...getSessionStorageItem(STORAGE_KEY, {}),
      ...urlParams,
    },
    onValuesChange: values => {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    },
  });

  const hasActiveFilters = Object.keys(omitBy(form.values, v => isEmptyish(v))).length > 0;

  const filterParams = useMemo(
    () =>
      omitBy(
        {
          ...form.values,
          dateAfter: form.values.dateAfter
            ? dayjs(form.values.dateAfter).format('YYYY-MM-DD')
            : null,
          dateBefore: form.values.dateBefore
            ? dayjs(form.values.dateBefore).format('YYYY-MM-DD')
            : null,
        },
        v => isEmptyish(v),
      ),
    [form.values],
  );

  const resetFilters = () => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    form.setValues(MAIL_FILTERS_INITIAL_VALUES);
  };

  return {
    form,
    hasActiveFilters,
    filterParams,
    resetFilters,
  };
};

const MAIL_FILTERS_INITIAL_VALUES: MailsSearchParams = {
  subject: '',
  body: '',
  fromMail: '',
  status: undefined,
  debtorId: '',
  externalMailboxId: '',
  accountManagerId: '',
  dateAfter: undefined,
  dateBefore: undefined,
  categories: [],
  sortBy: 'date',
  sortOrder: 'desc',
};
